import { useEffect } from 'react';

import { userModel } from '@/entities/user';

import api from '../api/api';
import {
  ENVIRONMENT,
  FORCE_HAPPYDESK,
  HAPPYDESK_IFRAME_SHOWN_CLASS,
} from '../config';
import { getName, isDemoSession } from '../lib';

import { useAppSelector } from './store';

const HAPPYDESK_SCRIPT_ID = 'happydesk-widget-loader';
const HAPPYDESK_SCRIPT_SRC = 'https://45hd2-widget.happydesk.ru/widget.js';
const HAPPYDESK_CLIENT_ID = 8518;
const HAPPYDESK_SERVER = 'https://45hd2-widget.happydesk.ru';
const HAPPYDESK_HOST = 'diagnocat.happydesk.ru';

type HappydeskLifecycleState = {
  activeClientUserId: string | null;
  anonymousModeRequested: boolean;
  iframeLoadPromise: Promise<void> | null;
  lifecycleAbortController: AbortController | null;
  pendingClientUserId: string | null;
  scriptLoadPromise: Promise<boolean> | null;
  userAccountKey: string | null;
  widgetInitialized: boolean;
  widgetInitAttempt: number;
  widgetInitPromise: Promise<boolean> | null;
  widgetInitSignal: AbortSignal | undefined;
};

const happydeskLifecycle: HappydeskLifecycleState = {
  activeClientUserId: null,
  anonymousModeRequested: true,
  iframeLoadPromise: null,
  lifecycleAbortController: null,
  pendingClientUserId: null,
  scriptLoadPromise: null,
  userAccountKey: null,
  widgetInitialized: false,
  widgetInitAttempt: 0,
  widgetInitPromise: null,
  widgetInitSignal: undefined,
};

type HappydeskUserAccount = {
  email: string;
  name: string;
  userId?: string;
};

type HappydeskWidget = NonNullable<Window['Happydesk']>;

const getHappydeskUserAccountKey = (userAccount: HappydeskUserAccount) => {
  return `${userAccount.userId ?? ''}:${userAccount.email}:${userAccount.name}`;
};

const resetHappydeskUserSession = () => {
  happydeskLifecycle.userAccountKey = null;
};

const abortHappydeskInitialization = () => {
  happydeskLifecycle.lifecycleAbortController?.abort();
  happydeskLifecycle.lifecycleAbortController = null;
  happydeskLifecycle.pendingClientUserId = null;
};

const destroyHappydeskWidget = (
  widget: HappydeskWidget | undefined = window.Happydesk,
) => {
  const container = widget?.iframe?.parentElement?.parentElement;
  container?.remove();

  if (window.Happydesk !== widget) {
    return;
  }

  document.getElementById(HAPPYDESK_SCRIPT_ID)?.remove();
  window.Happydesk = undefined;

  happydeskLifecycle.widgetInitialized = false;
  happydeskLifecycle.widgetInitPromise = null;
  happydeskLifecycle.widgetInitSignal = undefined;
  happydeskLifecycle.iframeLoadPromise = null;
  happydeskLifecycle.scriptLoadPromise = null;
  resetHappydeskUserSession();
};

export const handleHappydeskLogout = () => {
  happydeskLifecycle.anonymousModeRequested = true;
  abortHappydeskInitialization();
  happydeskLifecycle.activeClientUserId = null;
  resetHappydeskUserSession();
  window.Happydesk?.closeWidget?.();
};

const setHappydeskUserAccountOnce = (userAccount: HappydeskUserAccount) => {
  const accountKey = getHappydeskUserAccountKey(userAccount);
  if (happydeskLifecycle.userAccountKey === accountKey) {
    return;
  }

  if (typeof window.Happydesk?.setUserAccount !== 'function') {
    return;
  }

  window.Happydesk.setUserAccount(userAccount.email, userAccount.name);
  happydeskLifecycle.userAccountKey = accountKey;
};

const loadHappydeskScript = () => {
  if (typeof window.Happydesk?.initChat === 'function') {
    return Promise.resolve(true);
  }

  if (happydeskLifecycle.scriptLoadPromise) {
    return happydeskLifecycle.scriptLoadPromise;
  }

  document.getElementById(HAPPYDESK_SCRIPT_ID)?.remove();

  const script = document.createElement('script');
  script.id = HAPPYDESK_SCRIPT_ID;
  script.src = HAPPYDESK_SCRIPT_SRC;
  script.async = true;
  script.defer = true;

  happydeskLifecycle.scriptLoadPromise = new Promise((resolve) => {
    const finish = (isReady: boolean) => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);

      if (!isReady) {
        script.remove();
        happydeskLifecycle.scriptLoadPromise = null;
      }

      resolve(isReady);
    };

    const handleLoad = () => {
      finish(typeof window.Happydesk?.initChat === 'function');
    };
    const handleError = () => finish(false);

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
  });

  document.body.appendChild(script);

  return happydeskLifecycle.scriptLoadPromise;
};

// Must be called right after initChat: a later subscriber misses the load event.
const watchHappydeskIframeLoad = () => {
  if (happydeskLifecycle.iframeLoadPromise) {
    return;
  }

  const iframe = window.Happydesk?.iframe;
  if (!iframe) {
    return;
  }

  happydeskLifecycle.iframeLoadPromise = new Promise((resolve) => {
    iframe.addEventListener('load', () => resolve(), { once: true });
  });
};

// HappyDesk schedules serverParams after iframe load. Sending the account from
// the load handler overwrites stored userData before chat registration starts.
const setupHappydeskUserAccount = async (
  userAccount: HappydeskUserAccount,
  signal: AbortSignal,
) => {
  await happydeskLifecycle.iframeLoadPromise;

  if (signal.aborted) {
    return false;
  }

  setHappydeskUserAccountOnce(userAccount);
  return true;
};

const initHappydeskWidget = (
  userId: string | undefined,
  signal?: AbortSignal,
) => {
  if (happydeskLifecycle.widgetInitialized) {
    return Promise.resolve(true);
  }

  if (happydeskLifecycle.widgetInitPromise) {
    if (!happydeskLifecycle.widgetInitSignal?.aborted) {
      return happydeskLifecycle.widgetInitPromise;
    }
    happydeskLifecycle.widgetInitPromise = null;
    happydeskLifecycle.widgetInitSignal = undefined;
  }

  const initAttempt = happydeskLifecycle.widgetInitAttempt + 1;
  happydeskLifecycle.widgetInitAttempt = initAttempt;

  const initPromise = (async () => {
    const isReady = await loadHappydeskScript();

    if (!isReady || signal?.aborted || happydeskLifecycle.widgetInitialized) {
      return happydeskLifecycle.widgetInitialized;
    }

    const widget = window.Happydesk;
    if (typeof widget?.initChat !== 'function') {
      return false;
    }

    try {
      await widget.initChat(
        {
          clientId: HAPPYDESK_CLIENT_ID,
          server: HAPPYDESK_SERVER,
          host: HAPPYDESK_HOST,
        },
        {
          language: 'ru',
          page_url: window.location.href,
          user_agent: window.navigator.userAgent,
          ...(userId && { user_id: userId }),
        },
      );

      if (signal?.aborted || window.Happydesk !== widget) {
        destroyHappydeskWidget(widget);
        return false;
      }

      happydeskLifecycle.widgetInitialized = true;
      watchHappydeskIframeLoad();
      return true;
    } catch {
      return false;
    } finally {
      if (happydeskLifecycle.widgetInitAttempt === initAttempt) {
        happydeskLifecycle.widgetInitPromise = null;
        happydeskLifecycle.widgetInitSignal = undefined;
      }
    }
  })();

  happydeskLifecycle.widgetInitPromise = initPromise;
  happydeskLifecycle.widgetInitSignal = signal;

  return happydeskLifecycle.widgetInitPromise;
};

const resolveHappydeskUserAccount = async ({
  fallbackEmail,
  fallbackName,
}: {
  fallbackEmail: string;
  fallbackName: string;
}): Promise<HappydeskUserAccount | null> => {
  const fallbackUserAccount = {
    email: fallbackEmail.trim(),
    name: fallbackName.trim(),
  };

  try {
    const resp = await api.hubspot.getHappydeskUser({});
    const userAccount = {
      email: resp.Email.trim() || fallbackUserAccount.email,
      name: resp.Name.trim() || fallbackUserAccount.name,
      userId: resp.UserID.trim() || undefined,
    };

    const hasRequiredData = Boolean(userAccount.email && userAccount.name);

    return hasRequiredData ? userAccount : null;
  } catch {
    const hasRequiredFallbackData = Boolean(
      fallbackUserAccount.email && fallbackUserAccount.name,
    );

    return hasRequiredFallbackData ? fallbackUserAccount : null;
  }
};

const initializeAnonymousHappydesk = async () => {
  happydeskLifecycle.anonymousModeRequested = true;

  if (
    happydeskLifecycle.widgetInitialized ||
    happydeskLifecycle.widgetInitPromise
  ) {
    return;
  }

  abortHappydeskInitialization();

  const abortController = new AbortController();
  happydeskLifecycle.lifecycleAbortController = abortController;

  try {
    await initHappydeskWidget(undefined, abortController.signal);
  } finally {
    if (happydeskLifecycle.lifecycleAbortController === abortController) {
      happydeskLifecycle.lifecycleAbortController = null;
    }
  }
};

const initializeAuthenticatedHappydesk = async ({
  clientUserId,
  fallbackEmail,
  fallbackName,
}: {
  clientUserId: string;
  fallbackEmail: string;
  fallbackName: string;
}) => {
  if (
    happydeskLifecycle.activeClientUserId === clientUserId ||
    happydeskLifecycle.pendingClientUserId === clientUserId
  ) {
    return;
  }

  happydeskLifecycle.anonymousModeRequested = false;
  abortHappydeskInitialization();

  const abortController = new AbortController();
  happydeskLifecycle.lifecycleAbortController = abortController;
  happydeskLifecycle.pendingClientUserId = clientUserId;

  destroyHappydeskWidget();

  try {
    const userAccount = await resolveHappydeskUserAccount({
      fallbackEmail,
      fallbackName,
    });

    if (abortController.signal.aborted || !userAccount) {
      return;
    }

    const isInitialized = await initHappydeskWidget(
      userAccount.userId,
      abortController.signal,
    );
    if (!isInitialized || abortController.signal.aborted) {
      return;
    }

    const isAccountSet = await setupHappydeskUserAccount(
      userAccount,
      abortController.signal,
    );
    if (!isAccountSet) {
      return;
    }

    happydeskLifecycle.activeClientUserId = clientUserId;
  } finally {
    const shouldInitializeAnonymous =
      abortController.signal.aborted &&
      happydeskLifecycle.anonymousModeRequested;

    if (happydeskLifecycle.lifecycleAbortController === abortController) {
      happydeskLifecycle.lifecycleAbortController = null;
    }
    if (happydeskLifecycle.pendingClientUserId === clientUserId) {
      happydeskLifecycle.pendingClientUserId = null;
    }
    if (shouldInitializeAnonymous) {
      initializeAnonymousHappydesk();
    }
  }
};

export const useHappydeskChat = () => {
  const user = useAppSelector(userModel.selectors.selectCurrentUser);
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);
  const userLoading = useAppSelector(userModel.selectors.selectLoading);
  const clientUserId = user.ID;
  const email = user.PersonalData?.Emails[0] ?? '';
  const firstName = user.PersonalData?.FirstName ?? '';
  const lastName = user.PersonalData?.LastName ?? '';
  const name = getName(firstName, lastName, { userLocale });

  const isLocalhost = ['localhost', '127.0.0.1'].includes(
    window.location.hostname,
  );
  const disableHappydesk =
    localStorage.getItem('disableHappydesk') === 'true' ||
    (!FORCE_HAPPYDESK && (ENVIRONMENT === 'development' || isLocalhost));

  useEffect(() => {
    if (disableHappydesk) {
      return;
    }

    const controller = new AbortController();
    let previousFocus = document.activeElement;

    document.addEventListener(
      'focusin',
      (event) => {
        if (
          event.target instanceof HTMLElement &&
          event.target !== window.Happydesk?.iframe
        ) {
          previousFocus = event.target;
        }
      },
      { signal: controller.signal },
    );

    window.addEventListener(
      'blur',
      () => {
        window.setTimeout(() => {
          const iframe = window.Happydesk?.iframe;
          if (
            controller.signal.aborted ||
            !iframe ||
            document.activeElement !== iframe ||
            iframe.parentElement?.parentElement?.classList.contains(
              HAPPYDESK_IFRAME_SHOWN_CLASS,
            )
          ) {
            return;
          }

          iframe.blur();
          if (
            previousFocus instanceof HTMLElement &&
            previousFocus.isConnected
          ) {
            previousFocus.focus({ preventScroll: true });
          }
        }, 0);
      },
      { signal: controller.signal },
    );

    return () => controller.abort();
  }, [disableHappydesk]);

  useEffect(() => {
    if (disableHappydesk) {
      return;
    }

    const isAuthenticatedProfileReady =
      userLoading === 'succeeded' && Boolean(clientUserId);
    if (isAuthenticatedProfileReady && !isDemoSession()) {
      initializeAuthenticatedHappydesk({
        clientUserId,
        fallbackEmail: email,
        fallbackName: name,
      });
      return;
    }

    const isAnonymousProfileReady =
      isAuthenticatedProfileReady ||
      (userLoading !== 'pending' && !clientUserId);
    if (isAnonymousProfileReady) {
      initializeAnonymousHappydesk();
    }
  }, [clientUserId, disableHappydesk, email, name, userLoading]);
};
